// src/Admin/CourseManagement.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../Firebase/firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import MultilingualInput from '../components/MultilingualInput/MultilingualInput';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import {
  Button,
  Table,
  Spinner,
  Form,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import './admin.css'; // Ensure you have this CSS file

const CourseManagement = () => {
  // **Translation Hook**
  const { t, i18n } = useTranslation();

  // **State Variables**
  const [courses, setCourses] = useState([]);
  const [courseForm, setCourseForm] = useState({
    title: { en: '', ar: '' },
    subtitle: { en: '', ar: '' },
    overview: { en: '', ar: '' },
    instructor: '',
    instructorBio: { en: '', ar: '' },
    duration: { amount: '', unit: 'days' },
    startDate: '',
    endDate: '',
    syllabus: {
      en: [],
      ar: [],
    },
    syllabusPoint: { en: '', ar: '' },
  });
  const [editingCourseId, setEditingCourseId] = useState(null);

  // **Modal State for Confirmations**
  const [modalData, setModalData] = useState({
    isVisible: false,
    title: '',
    body: '',
    onConfirm: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // **Fetch Courses**
  const fetchCourses = useCallback(async () => {
    try {
      const coursesCollection = collection(db, 'courses');
      const querySnapshot = await getDocs(coursesCollection);
      const coursesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('errorFetchingCourses'));
    }
  }, [t]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // **Handle Course Form Changes**
  const handleCourseFormChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split('_');

    if (nameParts[0] === 'syllabusPoint') {
      const lang = nameParts[1];
      setCourseForm((prev) => ({
        ...prev,
        syllabusPoint: {
          ...prev.syllabusPoint,
          [lang]: value,
        },
      }));
    } else if (nameParts[0] === 'duration') {
      const durationField = nameParts[1];
      setCourseForm((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          [durationField]: value,
        },
      }));
    } else if (nameParts.length === 2) {
      const [field, lang] = nameParts;
      if (field in courseForm && typeof courseForm[field] === 'object') {
        setCourseForm((prev) => ({
          ...prev,
          [field]: {
            ...prev[field],
            [lang]: value,
          },
        }));
      }
    } else {
      setCourseForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // **Form Validation**
  const validateForm = () => {
    const errors = {};

    // Required Multilingual Fields
    ['title', 'subtitle', 'overview'].forEach((field) => {
      if (!(courseForm[field].en && courseForm[field].en.trim())) {
        errors[`${field}.en`] = t(`${field}.en.required`);
      }
      if (!(courseForm[field].ar && courseForm[field].ar.trim())) {
        errors[`${field}.ar`] = t(`${field}.ar.required`);
      }
    });

    // Additional Validations
    if (
      courseForm.duration.amount &&
      (isNaN(courseForm.duration.amount) ||
        Number(courseForm.duration.amount) < 0)
    ) {
      errors['duration.amount'] = t('invalidDurationAmount');
    }

    // Add more validations as needed

    return errors;
  };

  // **Handle Save Course**
  const handleSaveCourse = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // Display errors
      Object.values(errors).forEach((error) => toast.error(error));
      return;
    }

    const {
      title,
      subtitle,
      overview,
      instructor,
      instructorBio,
      duration,
      startDate,
      endDate,
      syllabus,
    } = courseForm;

    setIsProcessing(true);

    try {
      const courseData = {
        title,
        subtitle,
        overview,
        instructor,
        instructorBio,
        duration,
        startDate,
        endDate,
        syllabus,
        timestamp: new Date(),
      };

      if (editingCourseId) {
        await updateDoc(doc(db, 'courses', editingCourseId), courseData);
        toast.success(t('courseUpdated'));
      } else {
        const newDocRef = doc(collection(db, 'courses'));
        await setDoc(newDocRef, courseData);
        toast.success(t('courseAdded'));
      }

      resetCourseForm();
      fetchCourses();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(`${t('failedToSaveCourse')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // **Reset Course Form**
  const resetCourseForm = () => {
    setCourseForm({
      title: { en: '', ar: '' },
      subtitle: { en: '', ar: '' },
      overview: { en: '', ar: '' },
      instructor: '',
      instructorBio: { en: '', ar: '' },
      duration: { amount: '', unit: 'days' },
      startDate: '',
      endDate: '',
      syllabus: {
        en: [],
        ar: [],
      },
      syllabusPoint: { en: '', ar: '' },
    });
    setEditingCourseId(null);
    toast.info(t('formResetSuccessfully'));
  };

  // **Add Syllabus Point**
  const handleAddSyllabusPoint = (lang) => {
    const { syllabusPoint } = courseForm;
    if (syllabusPoint[lang].trim() !== '') {
      setCourseForm((prev) => ({
        ...prev,
        syllabus: {
          ...prev.syllabus,
          [lang]: [...prev.syllabus[lang], syllabusPoint[lang]],
        },
        syllabusPoint: {
          ...prev.syllabusPoint,
          [lang]: '',
        },
      }));
      toast.success(t('syllabusPointAdded'));
    } else {
      toast.warn(t('syllabusPointEmpty'));
    }
  };

  // **Remove Syllabus Point**
  const handleRemoveSyllabusPoint = (lang, index) => {
    setCourseForm((prev) => ({
      ...prev,
      syllabus: {
        ...prev.syllabus,
        [lang]: prev.syllabus[lang].filter((_, i) => i !== index),
      },
    }));
    toast.info(t('syllabusPointRemoved'));
  };

  // **Edit Course**
  const handleEditCourse = (course) => {
    setCourseForm({
      title: course.title || { en: '', ar: '' },
      subtitle: course.subtitle || { en: '', ar: '' },
      overview: course.overview || { en: '', ar: '' },
      instructor: course.instructor || '',
      instructorBio: course.instructorBio || { en: '', ar: '' },
      duration: course.duration || { amount: '', unit: 'days' },
      startDate: course.startDate || '',
      endDate: course.endDate || '',
      syllabus: course.syllabus || { en: [], ar: [] },
      syllabusPoint: { en: '', ar: '' },
    });
    setEditingCourseId(course.id);
    toast.info(t('editingCourse'));
  };

  // **Delete Course with Modal Confirmation**
  const handleDeleteCourse = (courseId) => {
    setModalData({
      isVisible: true,
      title: t('deleteCourse'),
      body: t('confirmDeleteCourse'),
      onConfirm: () => confirmDeleteCourse(courseId),
    });
  };

  const confirmDeleteCourse = async (courseId) => {
    setIsProcessing(true);
    try {
      await deleteDoc(doc(db, 'courses', courseId));
      toast.success(t('courseDeleted'));
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(`${t('failedToDeleteCourse')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setModalData((prev) => ({ ...prev, isVisible: false }));
    }
  };

  // **Handle Modal Cancellation**
  const handleCancelModal = () => {
    setModalData({
      isVisible: false,
      title: '',
      body: '',
      onConfirm: null,
    });
  };

  // **Rendering the Component**
  return (
    <section id="courses-section" className="admin-section">
      <h2 className="mb-4">{t('manageCourses')}</h2>
      <Card className="mb-5">
        <Card.Body>
          <Form onSubmit={handleSaveCourse}>
            {/* Course Form Fields */}
            <MultilingualInput
              label={t('title')}
              field="title"
              formData={courseForm.title}
              onChange={handleCourseFormChange}
              placeholder={{
                en: t('englishTitlePlaceholder'),
                ar: t('arabicTitlePlaceholder'),
              }}
              required
            />

            <MultilingualInput
              label={t('subtitle')}
              field="subtitle"
              formData={courseForm.subtitle}
              onChange={handleCourseFormChange}
              placeholder={{
                en: t('englishSubtitlePlaceholder'),
                ar: t('arabicSubtitlePlaceholder'),
              }}
              required
            />

            <MultilingualInput
              label={t('overview')}
              field="overview"
              formData={courseForm.overview}
              onChange={handleCourseFormChange}
              placeholder={{
                en: t('englishOverviewPlaceholder'),
                ar: t('arabicOverviewPlaceholder'),
              }}
              required
              isTextArea
              rows={4}
            />

            {/* Instructor Field */}
            <Form.Group className="mb-3" controlId="instructor">
              <Form.Label>{t('instructor')}</Form.Label>
              <Form.Control
                type="text"
                name="instructor"
                placeholder={t('professionalInstructorPlaceholder')}
                value={courseForm.instructor}
                onChange={handleCourseFormChange}
              />
            </Form.Group>

            {/* Instructor Bio Fields */}
            <MultilingualInput
              label={t('instructorBio')}
              field="instructorBio"
              formData={courseForm.instructorBio}
              onChange={handleCourseFormChange}
              placeholder={{
                en: t('englishInstructorBioPlaceholder'),
                ar: t('arabicInstructorBioPlaceholder'),
              }}
              isTextArea
              rows={2}
            />

            {/* Duration Field */}
            <Form.Group className="mb-3">
              <Form.Label>
                {t('duration')} <span className="text-danger">*</span>
              </Form.Label>
              <Row>
                <Col md={4}>
                  <Form.Control
                    type="number"
                    name="duration_amount"
                    placeholder={t('durationAmountPlaceholder')}
                    value={courseForm.duration.amount}
                    onChange={handleCourseFormChange}
                    min="0"
                    required
                    isInvalid={
                      courseForm.duration.amount &&
                      (isNaN(courseForm.duration.amount) ||
                        Number(courseForm.duration.amount) < 0)
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {t('invalidDurationAmount')}
                  </Form.Control.Feedback>
                </Col>
                <Col md={8}>
                  <Form.Select
                    name="duration_unit"
                    value={courseForm.duration.unit}
                    onChange={handleCourseFormChange}
                    required
                  >
                    <option value="days">{t('days')}</option>
                    <option value="months">{t('months')}</option>
                    <option value="years">{t('years')}</option>
                  </Form.Select>
                </Col>
              </Row>
            </Form.Group>

            {/* Date Fields */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="startDate">
                  <Form.Label>{t('startDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={courseForm.startDate}
                    onChange={handleCourseFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="endDate">
                  <Form.Label>{t('endDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={courseForm.endDate}
                    onChange={handleCourseFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Syllabus */}
            <div className="mb-3">
              <Form.Label>{t('syllabus')}</Form.Label>

              {/* English Syllabus */}
              <div className="mb-3">
                <h5>{t('english')}:</h5>
                <div className="input-group mb-2">
                  <Form.Control
                    type="text"
                    name="syllabusPoint_en"
                    placeholder={t('addEnglishSyllabusPoint')}
                    value={courseForm.syllabusPoint.en}
                    onChange={handleCourseFormChange}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => handleAddSyllabusPoint('en')}
                    aria-label="Add English Syllabus Point"
                  >
                    {t('add')}
                  </Button>
                </div>
                <ul className="list-group">
                  {courseForm.syllabus.en.length > 0 ? (
                    courseForm.syllabus.en.map((point, index) => (
                      <li
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {point}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveSyllabusPoint('en', index)}
                          aria-label={`Remove syllabus point ${index + 1}`}
                        >
                          &times;
                        </Button>
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item">{t('noSyllabusPoints')}</li>
                  )}
                </ul>
              </div>

              {/* Arabic Syllabus */}
              <div className="mb-3">
                <h5>{t('arabic')}:</h5>
                <div className="input-group mb-2">
                  <Form.Control
                    type="text"
                    name="syllabusPoint_ar"
                    placeholder={t('addArabicSyllabusPoint')}
                    value={courseForm.syllabusPoint.ar}
                    onChange={handleCourseFormChange}
                    dir="rtl"
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => handleAddSyllabusPoint('ar')}
                    aria-label="Add Arabic Syllabus Point"
                  >
                    {t('add')}
                  </Button>
                </div>
                <ul className="list-group">
                  {courseForm.syllabus.ar.length > 0 ? (
                    courseForm.syllabus.ar.map((point, index) => (
                      <li
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        {point}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveSyllabusPoint('ar', index)}
                          aria-label={`Remove syllabus point ${index + 1}`}
                        >
                          &times;
                        </Button>
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item">{t('noSyllabusPoints')}</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Form Actions */}
            <div className="d-flex gap-3">
              <Button
                variant="primary"
                type="submit"
                disabled={isProcessing}
                aria-label={editingCourseId ? 'Update Course' : 'Add Course'}
              >
                {isProcessing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{' '}
                    {editingCourseId ? t('updating') : t('adding')}
                  </>
                ) : editingCourseId ? (
                  t('updateCourse')
                ) : (
                  t('addCourse')
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={resetCourseForm}
                disabled={isProcessing}
                aria-label="Reset Form"
              >
                {t('resetForm')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Courses Table */}
      <h2 className="mb-4">{t('coursesList')}</h2>
      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-dark">
                <tr>
                  <th>{t('title')}</th>
                  <th>{t('instructor')}</th>
                  <th>{t('duration')}</th>
                  <th>{t('dates')}</th>
                  <th>{t('syllabus')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <tr key={course.id}>
                      <td>{course.title[i18n.language] || course.title['en']}</td>
                      <td>{course.instructor || t('notAvailable')}</td>
                      <td>
                        {course.duration.amount
                          ? `${course.duration.amount} ${t(course.duration.unit)}`
                          : t('notSpecified')}
                      </td>
                      <td>
                        {course.startDate
                          ? new Date(course.startDate).toLocaleDateString(
                              i18n.language
                            )
                          : t('noDate')}{' '}
                        -{' '}
                        {course.endDate
                          ? new Date(course.endDate).toLocaleDateString(
                              i18n.language
                            )
                          : t('noDate')}
                      </td>
                      <td>
                        <strong>{t('english')}:</strong>
                        {course.syllabus.en && course.syllabus.en.length > 0 ? (
                          <ul className="mb-2">
                            {course.syllabus.en.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mb-2">{t('noSyllabus')}</p>
                        )}
                        <strong>{t('arabic')}:</strong>
                        {course.syllabus.ar && course.syllabus.ar.length > 0 ? (
                          <ul>
                            {course.syllabus.ar.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>{t('noSyllabus')}</p>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditCourse(course)}
                          aria-label="Edit Course"
                        >
                          {t('edit')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteCourse(course.id)}
                          aria-label="Delete Course"
                        >
                          {t('delete')}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {t('noCoursesAvailable')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      {modalData.isVisible && (
        <ConfirmationModal
          isVisible={modalData.isVisible}
          title={modalData.title}
          body={modalData.body}
          onConfirm={modalData.onConfirm}
          onCancel={handleCancelModal}
          isProcessing={isProcessing}
        />
      )}

      {/* Toast Container for Notifications */}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
    </section>
  );
};

export default CourseManagement;
